"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentRoutes = void 0;
exports.getAbsoluteDocumentUrl = getAbsoluteDocumentUrl;
exports.getDocumentUrl = getDocumentUrl;
exports.parseDocumentPathname = parseDocumentPathname;
exports.getDocumentUrlFromUid = getDocumentUrlFromUid;
exports.getAbsoluteDocumentUrlFromUid = getAbsoluteDocumentUrlFromUid;
exports.queryParamValueForEditorState = queryParamValueForEditorState;
exports.buildDocumentPathFromSlugs = buildDocumentPathFromSlugs;
exports.getRelativeUrl = getRelativeUrl;
exports.getRelativeUrlFromDocumentName = getRelativeUrlFromDocumentName;
const models_1 = require("@buildwithflux/models");
const error_1 = require("./error");
const origin_1 = require("./origin");
const path_1 = require("./path");
const slug_1 = require("./slug");
const PROJECT_BY_UID = "/projects/:documentUid";
/**
 * A list of all possible document routes
 *
 * Order matters here, otherwise 'enterprise' may be interpreted as an owner handle, the enterprise handle interpreted
 * as a document slug, and the project slug interpreted as a local component slug etc.
 *
 * History routes are placed before their base routes to ensure they match first when the /history suffix is present.
 */
exports.DocumentRoutes = [
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug/history",
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug",
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:baseSlug/:componentSlug",
    "/enterprise/:enterpriseHandle/:documentSlug/history",
    "/enterprise/:enterpriseHandle/:documentSlug",
    "/enterprise/:enterpriseHandle/:baseSlug/:componentSlug",
    PROJECT_BY_UID,
    "/:handle/:documentSlug/history",
    "/:handle/:documentSlug",
    "/:handle/:baseSlug/:componentSlug",
];
/**
 * Main function for getting the absolute canonical URL for a document
 */
function getAbsoluteDocumentUrl(document, options = {}) {
    return (0, origin_1.toAbsoluteUrl)(getDocumentUrl(document, options));
}
/**
 * Main function for getting the relative canonical URL for a document
 */
function getDocumentUrl(document, options = {}) {
    /*
     * Requirements for reverse routing by slug:
     *  - For local components: need to have a baseSlugForLocalComponent passed to this function to receive routing based
     *      on their slug; we can't resolve the required base slug from the IDocumentData itself
     *  - For enterprise-owned documents: need to have the enterprise_owner_handle set on the IDocumentData
     *  - For all documents: need to have a slug and owner_handle set
     *
     * If any of these aren't the case, we can use the fallback of routing by UID (as long as the document has a UID)
     */
    if ((document.enterprise_owner_uid && !document.enterprise_owner_handle) ||
        ((document.localToDocumentUid || (0, slug_1.isLocalSlug)(document.slug)) && !options.baseSlugForLocalComponent) ||
        !document.slug ||
        !document.owner_handle) {
        if (!document.uid) {
            throw new Error("Could not generate URL for project with no UID");
        }
        return getDocumentUrlFromUid(document.uid, options);
    }
    /*
     * The normal case: build a URL using slugs of various kinds
     */
    return getDocumentUrlFromSlug(document, options);
}
/**
 * Parses a pathname (e.g. `location.pathname` or `new URL("...").pathname`) to extract the document information
 *
 * Supports all the same URLS generated by `getDocumentUrl`:
 *  - /projects/:uid
 * And for these formats, supports an additional local component slug on the end
 *  - /:userOrOrganizationHandle/:slug
 *  - /enterprise/:enterpriseHandle/:slug
 *  - /enterprise/:enterpriseHandle/-/:enterpriseOrganizationHandle/:slug
 *
 * To load a document from the parsed information, see loadDocumentBaseDataFromParsedPath()
 */
function parseDocumentPathname(pathname) {
    // Strip the /history suffix if present - it's a special route for change history deep links
    // and should not be treated as a localComponentSlug
    const pathnameWithoutHistory = pathname.endsWith("/history") ? pathname.slice(0, -"/history".length) : pathname;
    const parts = pathnameWithoutHistory.split("/").filter((part) => part.length > 0);
    if (parts?.[0] === "projects") {
        const [_projectsPrefix, documentUid] = parts;
        const documentUidParsed = models_1.DocumentUid.safeParse(documentUid);
        if (!documentUidParsed.success) {
            throw new error_1.UrlParsingError("Invalid document UID in pathname", documentUidParsed.error);
        }
        return { uid: documentUidParsed.data };
    }
    if (parts?.[0] === "enterprise" && parts?.[2] === "-") {
        const [_enterprisePrefix, enterpriseHandle, _dash, enterpriseOrganizationHandle, slug, localComponentSlug] = parts;
        if (!slug) {
            throw new error_1.UrlParsingError("Missing slug in pathname, expected in fourth position");
        }
        const enterpriseHandleParsed = models_1.EnterpriseHandle.safeParse(enterpriseHandle);
        if (!enterpriseHandleParsed.success) {
            throw new error_1.UrlParsingError("Invalid enterprise handle in pathname", enterpriseHandleParsed.error);
        }
        const enterpriseOrganizationHandleParsed = models_1.OrganizationPrivateHandle.safeParse(enterpriseOrganizationHandle);
        if (!enterpriseOrganizationHandleParsed.success) {
            throw new error_1.UrlParsingError("Invalid enterprise handle in pathname", enterpriseOrganizationHandleParsed.error);
        }
        return {
            enterpriseHandle: enterpriseHandleParsed.data,
            enterpriseOrganizationHandle: enterpriseOrganizationHandleParsed.data,
            slug,
            localComponentSlug,
        };
    }
    if (parts?.[0] === "enterprise") {
        const [_enterprisePrefix, enterpriseHandle, slug, localComponentSlug] = parts;
        if (!slug) {
            throw new error_1.UrlParsingError("Missing slug in pathname, expected in third position");
        }
        const enterpriseHandleParsed = models_1.EnterpriseHandle.safeParse(enterpriseHandle);
        if (!enterpriseHandleParsed.success) {
            throw new error_1.UrlParsingError("Invalid enterprise handle in pathname", enterpriseHandleParsed.error);
        }
        return {
            enterpriseHandle: enterpriseHandleParsed.data,
            enterpriseOrganizationHandle: undefined,
            slug,
            localComponentSlug,
        };
    }
    const [userOrOrganizationHandle, slug, localComponentSlug] = parts;
    if (!userOrOrganizationHandle || !slug) {
        throw new error_1.UrlParsingError("Pathname must have at least two parts to be a valid document pathname");
    }
    return {
        userOrOrganizationHandle,
        slug,
        localComponentSlug,
    };
}
// ----- Constituent functions of the above main entrypoint functions:
function getDocumentUrlFromUid(documentUid, options = {}) {
    return `${PROJECT_BY_UID.replace(":documentUid", documentUid.toLowerCase())}${getUrlParams(options)}`;
}
function getAbsoluteDocumentUrlFromUid(documentUid, options = {}) {
    return (0, origin_1.toAbsoluteUrl)(getDocumentUrlFromUid(documentUid, options));
}
function getDocumentUrlFromSlug(document, options) {
    const urlParts = ["/"];
    if (document.enterprise_owner_uid) {
        if (!document.enterprise_owner_handle) {
            // This won't usually happen, because callers should go through getDocumentUrl, which protects against this code path
            throw new error_1.ReverseUrlRoutingError("Cannot route to an enterprise-owned project without an enterprise_owner_handle");
        }
        urlParts.push("enterprise", document.enterprise_owner_handle);
        if (!document.organization_owner_is_enterprise_default) {
            urlParts.push("-", document.owner_handle);
        }
    }
    else {
        urlParts.push(document.owner_handle);
    }
    if ((0, slug_1.isLocalSlug)(document.slug)) {
        if (!options.baseSlugForLocalComponent) {
            // This won't usually happen, because callers should go through getDocumentUrl, which protects against this code path
            throw new error_1.ReverseUrlRoutingError("Cannot create local document URL without a baseSlug");
        }
        urlParts.push(options.baseSlugForLocalComponent, document.slug.slice("local:".length));
    }
    else {
        urlParts.push(document.slug);
    }
    return `${(0, path_1.joinPathParts)(...urlParts)}${getUrlParams(options)}`;
}
function getUrlParams(options) {
    if (Object.entries(options?.urlParams ?? {}).length === 0) {
        return "";
    }
    const searchParams = new URLSearchParams();
    if (options?.urlParams?.embed) {
        searchParams.set("embed", "1");
    }
    if (options?.urlParams?.editorMode) {
        searchParams.set("editor", queryParamValueForEditorState(options.urlParams ?? {}));
    }
    if (options?.urlParams?.fit) {
        searchParams.set("fit", options?.urlParams?.fit);
    }
    if (options?.urlParams?.doc) {
        searchParams.set("doc", options.urlParams.doc);
    }
    return `?${searchParams.toString()}`;
}
function queryParamValueForEditorState(params) {
    if (params.editorMode === models_1.EditorModes.pcb) {
        if (params.cameraMode === models_1.CameraMode.two_d) {
            return "pcb_2d";
        }
        else {
            return "pcb_3d";
        }
    }
    return params.editorMode ?? models_1.EditorModes.schematic;
}
// ----- Deprecated functions that should be cleaned up
function buildDocumentPathFromSlugs(baseSlug, componentSlug) {
    return (0, path_1.joinPathParts)(baseSlug, componentSlug).toLowerCase();
}
/**
 * @deprecated Does not support enterprises
 */
function getRelativeUrl(documentUid, documentSlug, documentOwnerHandle, baseSlug, params = {}) {
    if (documentOwnerHandle && (!(0, slug_1.isLocalSlug)(documentSlug) || baseSlug)) {
        return getRelativeUrlFromSlug(documentOwnerHandle, documentSlug, baseSlug, params);
    }
    else if (documentUid) {
        return getDocumentUrlFromUid(documentUid);
    }
    else {
        return "/"; // QUESTION: what is this for?
    }
}
/**
 * NOTE: The slugify feature was changed in #3260 in Nov 2022 without migrating
 * the stored data, so some links generated by
 * getRelativeUrlFromDocumentName will be invalid and return 404.
 *
 * @see getDocumentUrlFromUid
 * @deprecated The slug is stored source-of-truth information on the document and cannot be calculated from the name
 * @deprecated Does not support local components
 * @deprecated Does not support enterprises
 */
function getRelativeUrlFromDocumentName(userHandle, documentName, params = {}) {
    return getRelativeUrlFromSlug(userHandle, (0, models_1.slugifyDocumentName)(documentName), undefined, params);
}
/**
 * @deprecated Does not support enterprises
 */
function getRelativeUrlFromSlug(ownerHandle, slug, baseSlug, params = {}) {
    let path;
    if ((0, slug_1.isLocalSlug)(slug)) {
        if (!baseSlug) {
            // Catch any callers that call this improperly on a local document
            throw new Error("Cannot create local document URL without baseSlug. Provide baseSlug or use uid-based URL instead");
        }
        path = (0, path_1.joinPathParts)("/", ownerHandle, baseSlug, slug.slice("local:".length));
    }
    else {
        path = (0, path_1.joinPathParts)("/", ownerHandle, slug);
    }
    if (params.editorMode || params.embed) {
        const searchParams = new URLSearchParams();
        if (params.editorMode) {
            searchParams.set("editor", queryParamValueForEditorState(params));
        }
        if (params.embed) {
            searchParams.set("embed", "1");
        }
        if (params.fit) {
            searchParams.set("fit", params.fit);
        }
        return path + "?" + searchParams.toString();
    }
    return path;
}
//# sourceMappingURL=document.js.map